import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {
  // Column,
  ColumnPinningState,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, {
  CSSProperties,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TableWrapper, widthCheckBox } from "./styles";
import CircularProgress from "@mui/material/CircularProgress";
import IndeterminateCheckbox from "./IndeterminateCheckbox";
import { useDispatch, useSelector } from "react-redux";
import { controlInfinitiveScrollSelector } from "../ListInfinitiveScrolling/redux/selector";
import { InfiniteScrollKey, LIMIT } from "../ListInfinitiveScrolling/const";
import {
  loadMoreList,
  setupControlPage,
} from "../ListInfinitiveScrolling/redux/controlInfinitiveScrolling";
import { useLegacyEffect } from "helper/customHook";
import { Tooltip } from "@mui/material";

export interface TableInfiniteScrollingProps {
  /**
   * Id để quản lý limit, offset
   * @default undefined
   */
  id?: string;
  /**
   * Dữ liệu cho bảng
   * @default []
   */
  dataTable?: any[];

  /**
   * Thiết lập cột cho bảng, mỗi cột được định nghĩa bởi đối tượng với cấu hình cụ thể.
   */
  columns: any[];

  /**
   * Cờ xác định xem có thể tải thêm dữ liệu khi cuộn đến cuối bảng hay không.
   * @default true
   */
  isHasMore?: boolean;

  /**
   * Trạng thái loading trước khi có dữ liệu.
   * @default false
   */
  isLoading?: boolean;

  /**
   * Hàm callback được gọi khi người dùng cuộn đến cuối bảng để tải thêm dữ liệu.
   * Hàm này không nhận tham số và không trả về giá trị.
   */
  fetchMore?: () => void;
  //====================================
  /**
   * Hàm callback được gọi khi người dùng cuộn đến cuối bảng để tải thêm dữ liệu.
   * Hàm này không nhận tham số và không trả về giá trị.
   */
  isSelected?: boolean;
  /**
   * Bật tắt chế độ Selection
   *  @default false
   */
  validateEnableRowSelection?: (row: any) => boolean;
  /**
   * @default true
   * enable hoặc disable Selection Row
   */
  rowSelected?: (rowList: any) => any;
  /**
   * @default true
   * Lấy dữ liệu ID row selected
   */
  onClickRow?: (dataRow: any) => void;
  // event khi nhấp chuột 1 lần của table
  onDoubleClickRow?: (dataRow: any) => void;
  // event khi nhấp chuột double
  isShowHeader?: boolean;
  /**
   * Bật tắt chế độ hiển thị footer cho bảng cần sử dụng
   *  @default true
   */
  isShowFooter?: boolean;
  /**
   * Bật tắt chế độ hiển thị footer cho bảng cần sử dụng
   *  @default false
   */
  columnPinning?: ColumnPinningState;
  /**
   * Điều chỉnh column nào muốn sticky pinned
   * VD columnPinning:{
   *  left: ["select", "firstName", "lastName"],
   *  right: ["createdAt"]
   * }
   *  @default {}
   */
  isBorderTable?: boolean;
  /**
   * Điều chỉnh tắt mở cái borderTable
   *  @default true
   */

  messageEmpty?: string | React.ReactNode;
  /**
   * Message hiển thị khi Không có dữ liệu
   */
  expanded?: ExpandedState;
  /**
   * Bật tắt chế độ hiển thị expand cho row
   * @default false
   *
   **/

  defaultLimit?: number;

  tooltip?: string;
  /**
   * Tooltip hiển thị khi hover vào row  */
}

const TableInfiniteScrolling = ({
  id,
  dataTable = [],
  columns,
  isHasMore,
  isLoading = false,
  fetchMore = () => {},
  isSelected = false,
  validateEnableRowSelection = () => true,
  rowSelected,
  onClickRow,
  onDoubleClickRow,
  isShowFooter = false,
  isShowHeader = true,
  isBorderTable = true,
  columnPinning = {},
  expanded,
  messageEmpty = "Không có dữ liệu",
  defaultLimit = LIMIT,
  tooltip = "",
}: TableInfiniteScrollingProps) => {
  const dispatch = useDispatch();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [rowSelection, setRowSelection] = React.useState({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isFetching, setIsFetching] = useState<boolean>(true);

  const { offset, limit } = useSelector(
    controlInfinitiveScrollSelector(id ?? InfiniteScrollKey.DEFAULT)
  );

  useEffect(() => {
    if (dataTable?.length > 0) {
      setIsFetching(false);
    }
    // isFetching ban đầu sẽ là True để active cái scroll
    // scroll => Trạng thái Fetching data  => chỉ khi có dữ liệu mới update => isFetching = false
  }, [dataTable]);

  useLegacyEffect(() => {
    if (id && id !== InfiniteScrollKey.DEFAULT) {
      dispatch(setupControlPage({ id, limit: defaultLimit }));
    }
  }, []);

  const handleFetchNextPage = () => {
    dispatch(loadMoreList({ id }));
    fetchMore();
    setIsFetching(true);
    // Hàm thực hiện fetchMore và update trạng thái đang lấy dữ liệu isFetching
  };

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      const hasMore = isHasMore ?? dataTable?.length === offset + limit;

      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          !isFetching &&
          hasMore
        ) {
          handleFetchNextPage();
          // Tính khoảng cách gần cuối  + isFetching = false + isHasMore chủ động tắt bật
        }
      }
    },

    [handleFetchNextPage, isHasMore, isFetching]
  );

  const columnsOptions = useMemo(() => {
    if (isSelected) {
      return [
        {
          id: "select",
          header: ({ table }) => (
            <IndeterminateCheckbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          ),
          cell: ({ row }) => (
            <div className="px-1" onClick={(e) => e.stopPropagation()}>
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            </div>
          ),
          footer: (props) => props.column.id,
          minSize: widthCheckBox,
          maxSize: widthCheckBox,
        },
        ,
        ...columns,
      ];
    }
    return columns;
  }, [columns]);

  const controlEnableRowSelection = (row) => {
    return validateEnableRowSelection(row);
  };

  useEffect(() => {
    rowSelected?.(rowSelection);
  }, [rowSelection]);

  const table = useReactTable({
    data: dataTable,
    columns: columnsOptions,
    state: {
      sorting,
      rowSelection,
      columnPinning,
      expanded: expanded,
    },
    getSubRows: (row) => row.children,
    // getIsRowExpanded: (row) => true,
    enableExpanding: true,
    // getPaginationRowModel: getPaginationRowModel(),
    // getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    // getExpandedRowModel: getExpandedRowModel(),
    enableRowSelection: controlEnableRowSelection, //enable row selection for all rows
    // enableRowSelection: row => row.original.age > 18, // or enable row selection conditionally
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    debugTable: true,
  });

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater);
    if (!!table.getRowModel().rows.length) {
      rowVirtualizer.scrollToIndex?.(0);
    }
  };
  //scroll to top of table when sorting changes
  //since this table option is derived from table row model state, we're using the table.setOptions utility

  table.setOptions((prev) => ({
    ...prev,
    onSortingChange: handleSortingChange,
  }));

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 33, //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    //measure dynamic row height, except in firefox because it measures table border height incorrectly
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  }); // Cấu hình Row Virtualizer

  const tableWidth = () => {
    if (isSelected) {
      return tableContainerRef?.current?.clientWidth - widthCheckBox || 1;
    }
    return tableContainerRef?.current?.clientWidth || 1;
  };
  const defaultPercentWidthCol =
    columns.length !== 0
      ? ((tableWidth() / columns.length) * 100) / tableWidth()
      : 0; // Công thức tính trung bình độ rộng col theo %
  // ((Độ rộng Table/ số col)* 100 ) / Độ rộng table

  const handleClickRow = (dataRow: any) => {
    onClickRow?.(dataRow);
  };

  const handleDoubleClickRow = (dataRow: any) => {
    onDoubleClickRow?.(dataRow);
  };

  const getCommonPinningStyles = (column: any): CSSProperties => {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === "left" && column.getIsLastColumn("left");
    const isFirstRightPinnedColumn =
      isPinned === "right" && column.getIsFirstColumn("right");

    return {
      boxShadow: isLastLeftPinnedColumn
        ? "-4px 0 4px -4px gray inset"
        : isFirstRightPinnedColumn
          ? "4px 0 4px -4px gray inset"
          : undefined,
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      right:
        isPinned === "right"
          ? `${column?.columnDef?.right || column.getAfter("right")}px`
          : undefined,
      opacity: 1,
      position: isPinned ? "sticky" : "relative",
      zIndex: isPinned ? 1 : 0,
      // background: isPinned ? "#fff" : "auto", // Background for pinned columns
    };
  };

  return (
    <>
      <TableWrapper
        onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
        ref={tableContainerRef}
        isBorderTable={isBorderTable}
        className="table-wrapper"
      >
        {/* Even though we're still using sematic table tags, we must use CSS grid and flexbox for dynamic row heights */}
        <table>
          {isShowHeader && (
            <thead>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header: any) => {
                      const column = header.column;
                      const colMinWidth = column.columnDef.minSize;
                      const colMaxWidth = column.columnDef.maxSize;
                      const isShowHeader =
                        table.getHeaderGroups().length === 1 ||
                        (table.getHeaderGroups().length > 1 &&
                          (header.subHeaders.length > 0 ||
                            header.column.parent));
                      return (
                        <th
                          key={header.id}
                          style={{
                            ...getCommonPinningStyles(column),
                            minWidth: colMinWidth + "px",
                            maxWidth: colMaxWidth + "px",
                            width: defaultPercentWidthCol + "%",
                            borderTop: header.column.parent
                              ? "1px solid #d1d5f5"
                              : "none",

                            ...(column.columnDef.headerStyle || {}), //  style cho thẻ th
                            borderRadius: header.column.parent
                              ? "none !important"
                              : "inherit",
                          }}
                        >
                          <div
                            {...{
                              className: column.getCanSort() ? "can-sort" : "",
                              onClick: column.getToggleSortingHandler(),
                            }}
                            style={{
                              ...(column.columnDef.customStyle || {}), // style cho thẻ div bên trong th
                            }}
                          >
                            {isShowHeader ? (
                              flexRender(
                                column.columnDef.header,
                                header.getContext()
                              )
                            ) : (
                              <></>
                            )}
                            {{
                              asc: <ArrowDropDownIcon></ArrowDropDownIcon>,
                              desc: <ArrowDropUpIcon></ArrowDropUpIcon>,
                            }[column.getIsSorted() as string] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
          )}

          <tbody
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`, //tells scrollbar how big the table is
              position: "relative", //needed for absolute positioning of rows
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index] as Row<any>;
              return (
                <>
                  <Tooltip
                    title={tooltip || ""}
                    key={row.id}
                    placement="top"
                    followCursor
                  >
                    <tr
                      data-index={virtualRow.index} //needed for dynamic row height measurement
                      ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
                      key={row.id}
                      style={{
                        display: "flex",
                        position: "absolute",
                        transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
                        width: "100%",
                        cursor: `${!!onClickRow ? "pointer" : "auto"}`,
                      }}
                      onClick={() => handleClickRow(row)}
                      onDoubleClick={() => handleDoubleClickRow(row)}
                      title={!!onClickRow ? "Click để xem chi tiết" : ""}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const column = cell.column;
                        const colMinWidth = column.columnDef.minSize;
                        const colMaxWidth = column.columnDef.maxSize;
                        return (
                          <td
                            key={cell.id}
                            style={{
                              ...getCommonPinningStyles(column),
                              minWidth: colMinWidth + "px",
                              maxWidth: colMaxWidth + "px",
                              width: defaultPercentWidthCol + "%",
                            }}
                          >
                            {flexRender(
                              column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </Tooltip>
                </>
              );
            })}
          </tbody>
          {isLoading && <CircularProgress />}
          {dataTable.length === 0 && <div>{messageEmpty}</div>}

          {isShowFooter && (
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => {
                    const column = header.column;
                    const colMinWidth = column.columnDef.minSize;
                    const colMaxWidth = column.columnDef.maxSize;
                    return (
                      <th
                        key={header.id}
                        style={{
                          ...getCommonPinningStyles(column),
                          minWidth: colMinWidth + "px",
                          maxWidth: colMaxWidth + "px",
                          width: defaultPercentWidthCol + "%",
                        }}
                      >
                        {flexRender(
                          column.columnDef.footer,
                          header.getContext()
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </TableWrapper>
    </>
  );
};

export default memo(TableInfiniteScrolling);
